import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  FileText, 
  Euro, 
  AlertTriangle, 
  TrendingUp,
  Users,
  Building
} from "lucide-react";
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend 
} from "recharts";

const RISK_COLORS = {
  verde: "#22c55e",
  medio: "#eab308",
  forte: "#f97316",
  molto_forte: "#ef4444",
  unknown: "#94a3b8"
};

const RISK_LABELS = {
  verde: "Verde (0-3 gg)",
  medio: "Medio (4-7 gg)",
  forte: "Forte (8-14 gg)",
  molto_forte: "Molto Forte (>14 gg)",
  unknown: "N/D"
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const KPICard = ({ title, value, subtitle, icon: Icon, color = "slate" }) => (
  <Card className="kpi-card border-slate-200" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, "-")}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">{title}</p>
          <p className="mt-2 text-3xl font-bold text-slate-900 font-heading">{value}</p>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const RiskBadge = ({ level }) => {
  const config = {
    verde: "bg-green-100 text-green-800 border-green-300",
    medio: "bg-yellow-100 text-yellow-800 border-yellow-300",
    forte: "bg-orange-100 text-orange-800 border-orange-300",
    molto_forte: "bg-red-100 text-red-800 border-red-300",
    unknown: "bg-slate-100 text-slate-800 border-slate-300"
  };
  
  return (
    <Badge className={`${config[level] || config.unknown} border`}>
      {RISK_LABELS[level] || level}
    </Badge>
  );
};

const DashboardOverview = ({ kpi, preventivi, filters }) => {
  if (!kpi) return null;

  // Prepare risk distribution data for pie chart
  const riskData = Object.entries(kpi.risk_distribution || {})
    .filter(([key, value]) => value > 0 && key !== "unknown")
    .map(([key, value]) => ({
      name: RISK_LABELS[key],
      value,
      color: RISK_COLORS[key]
    }));

  // Prepare TC performance data for bar chart
  const tcData = Object.entries(kpi.by_tc || {})
    .slice(0, 10)
    .map(([tc, data]) => ({
      name: tc.length > 15 ? tc.substring(0, 15) + "..." : tc,
      fullName: tc,
      preventivi: data.count,
      valore: data.value / 1000
    }))
    .sort((a, b) => b.valore - a.valore);

  // Calculate dominant risk
  const totalPreventivi = kpi.total_preventivi || 0;
  const riskPercent = totalPreventivi > 0 ? {
    verde: ((kpi.risk_distribution?.verde || 0) / totalPreventivi * 100).toFixed(0),
    medio: ((kpi.risk_distribution?.medio || 0) / totalPreventivi * 100).toFixed(0),
    forte: ((kpi.risk_distribution?.forte || 0) / totalPreventivi * 100).toFixed(0),
    molto_forte: ((kpi.risk_distribution?.molto_forte || 0) / totalPreventivi * 100).toFixed(0)
  } : {};

  const highRiskPercent = parseInt(riskPercent.forte || 0) + parseInt(riskPercent.molto_forte || 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-overview">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Totale Preventivi"
          value={kpi.total_preventivi?.toLocaleString("it-IT") || 0}
          subtitle={`${Object.keys(kpi.by_tc || {}).length} Tecnici Commerciali`}
          icon={FileText}
          color="slate"
        />
        <KPICard
          title="Valore Totale"
          value={formatCurrency(kpi.total_value || 0)}
          subtitle={`Media: ${formatCurrency(kpi.avg_value || 0)}`}
          icon={Euro}
          color="orange"
        />
        <KPICard
          title="Rischio Alto"
          value={`${highRiskPercent}%`}
          subtitle={`${(kpi.risk_distribution?.forte || 0) + (kpi.risk_distribution?.molto_forte || 0)} preventivi a rischio`}
          icon={AlertTriangle}
          color="red"
        />
        <KPICard
          title="Sedi Attive"
          value={Object.keys(kpi.by_sede || {}).length}
          subtitle={`${Object.keys(kpi.by_tc || {}).length} TC totali`}
          icon={Building}
          color="blue"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Distribution Pie Chart */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight">
              Distribuzione Rischio
            </CardTitle>
          </CardHeader>
          <CardContent>
            {riskData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={riskData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {riskData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`${value} preventivi`, ""]}
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span className="text-sm">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>

        {/* TC Performance Bar Chart */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-tight">
              Top 10 TC per Valore
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tcData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tcData} layout="vertical" margin={{ left: 20, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tickFormatter={(v) => `${v}k€`} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === "valore" ? formatCurrency(value * 1000) : value,
                        name === "valore" ? "Valore" : "Preventivi"
                      ]}
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                    />
                    <Bar dataKey="valore" fill="#f97316" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Risk Summary by Level */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg uppercase tracking-tight">
            Riepilogo per Livello di Rischio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["verde", "medio", "forte", "molto_forte"].map((level) => {
              const count = kpi.risk_distribution?.[level] || 0;
              const percent = totalPreventivi > 0 ? ((count / totalPreventivi) * 100).toFixed(1) : 0;
              return (
                <div 
                  key={level}
                  className={`p-4 rounded-lg border-2`}
                  style={{ 
                    backgroundColor: RISK_COLORS[level] + "15",
                    borderColor: RISK_COLORS[level] + "50"
                  }}
                  data-testid={`risk-summary-${level}`}
                >
                  <RiskBadge level={level} />
                  <p className="mt-3 text-2xl font-bold font-heading">{count}</p>
                  <p className="text-sm text-slate-600">{percent}% del totale</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent Preventivi Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg uppercase tracking-tight">
            Ultimi Preventivi Caricati
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-preventivi-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">TC</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Soggetto</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Sede</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-600">Data</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-600">Valore</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-600">Rischio</th>
                </tr>
              </thead>
              <tbody>
                {preventivi.slice(0, 5).map((p, i) => (
                  <tr 
                    key={p.id} 
                    className={`border-b border-slate-100 hover:bg-slate-50 row-risk-${p.risk_level?.replace("_", "-")}`}
                  >
                    <td className="py-3 px-4 font-medium">{p.tc}</td>
                    <td className="py-3 px-4">{p.soggetto}</td>
                    <td className="py-3 px-4">{p.sede}</td>
                    <td className="py-3 px-4">{p.data_creazione}</td>
                    <td className="py-3 px-4 text-right font-medium">{formatCurrency(p.valore_preventivo)}</td>
                    <td className="py-3 px-4 text-center">
                      <RiskBadge level={p.risk_level} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardOverview;
